import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, LayoutGrid, Table2, ListChecks, FileText, Plus } from "lucide-react";
import { useApp } from "../context/AppContext";
import { PROJECT_STATUSES, STATUS_STYLE, fmtDate } from "../lib/api";
import { Avatar, HealthPill, PageHeader, PriorityPill, Progress, Segmented, StatusPill, Card } from "../components/pm/bits";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

export default function Projects() {
  const { projects, user, updateProject, openDialog } = useApp();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");
  const [view, setView] = useState("kanban");
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);

  const mine = projects.filter((p) => p.pmId === user?.id);
  const list = (scope === "mine" ? mine : projects).filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const cols = PROJECT_STATUSES.filter((s) => ["Not Started", "In-Progress", "Completed"].includes(s) || list.some((p) => p.status === s));

  const drop = (status) => {
    const p = projects.find((x) => x.id === dragId);
    if (p && p.status !== status) updateProject(p.id, { status });
    setDragId(null);
    setOver(null);
  };

  return (
    <div className="mx-auto max-w-7xl animate-fade-in-up">
      <PageHeader title="Projects" subtitle={`${projects.length} live projects`}>
        <button onClick={() => openDialog("project")} className="flex h-9 items-center gap-1.5 rounded-lg bg-[#00573f] px-3 text-sm font-semibold text-white hover:bg-[#004330]" data-testid="add-project-btn">
          <Plus className="h-4 w-4" /> New Project
        </button>
      </PageHeader>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="projects-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects..." className="h-9 bg-white pl-9" />
        </div>
        <Segmented testid="projects-scope" value={scope} onChange={setScope} options={[{ value: "all", label: "All projects" }, { value: "mine", label: `My projects (${mine.length})` }]} />
        <div className="ml-auto">
          <Segmented testid="projects-view" value={view} onChange={setView} options={[{ value: "kanban", label: "Kanban", icon: LayoutGrid }, { value: "table", label: "Table", icon: Table2 }]} />
        </div>
      </div>

      {view === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {cols.map((s) => {
            const items = list.filter((p) => p.status === s);
            return (
              <div
                key={s}
                onDragOver={(e) => { e.preventDefault(); setOver(s); }}
                onDragLeave={() => setOver(null)}
                onDrop={() => drop(s)}
                className={`w-[340px] shrink-0 rounded-2xl p-2.5 transition-colors ${over === s ? "bg-[#f5efe3] ring-2 ring-[#b8975a]/50" : "bg-slate-100/70"}`}
                data-testid={`project-col-${s}`}
              >
                <div className="mb-2 flex items-center justify-between px-2 py-1.5">
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_STYLE[s].dot }} /> {s}
                  </span>
                  <span className="rounded-md bg-white px-2 py-0.5 text-xs font-semibold text-slate-500">{items.length}</span>
                </div>
                <div className="space-y-2.5">
                  {items.map((p) => (
                    <div
                      key={p.id}
                      draggable
                      onDragStart={() => setDragId(p.id)}
                      onClick={() => nav(`/projects/${p.id}`)}
                      data-testid={`project-card-${p.id}`}
                      className={`cursor-pointer rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-shadow hover:border-[#e7dcc6] hover:shadow-md ${dragId === p.id ? "opacity-50" : ""}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-[#a08450]">{p.code}</p>
                          <p className="mt-0.5 text-[15px] font-semibold leading-snug text-slate-900">{p.name}</p>
                        </div>
                        <PriorityPill priority={p.priority} />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        <StatusPill status={p.status} />
                        {p.health !== "On Track" && <HealthPill health={p.health} />}
                      </div>
                      <div className="mt-3">
                        <div className="mb-1 flex justify-between text-xs text-slate-500"><span>Progress</span><span>{p.progress}%</span></div>
                        <Progress value={p.progress} />
                      </div>
                      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                        <div className="flex items-center gap-3 text-xs text-slate-400">
                          <span className="flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> {p.tasksTotal}</span>
                          <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {p.docsCount}</span>
                          <span>{fmtDate(p.dueDate)}</span>
                        </div>
                        <Avatar person={p.pmAvatar} size={26} />
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <p className="px-2 py-6 text-center text-xs text-slate-400">Drop projects here</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="projects-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {["Project", "Status", "Priority", "Health", "Manager", "Start", "End", "Tasks", "Progress"].map((h) => (
                    <TableHead key={h} className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-[#fbf8f1]" onClick={() => nav(`/projects/${p.id}`)}>
                    <TableCell>
                      <p className="text-xs font-semibold text-[#a08450]">{p.code}</p>
                      <p className="font-semibold text-slate-900">{p.name}</p>
                    </TableCell>
                    <TableCell><StatusPill status={p.status} /></TableCell>
                    <TableCell><PriorityPill priority={p.priority} /></TableCell>
                    <TableCell><HealthPill health={p.health} /></TableCell>
                    <TableCell><div className="flex items-center gap-2 whitespace-nowrap"><Avatar person={p.pmAvatar} size={24} /> <span className="text-sm">{p.pm || "–"}</span></div></TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-slate-600">{fmtDate(p.startDate)}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-slate-600">{fmtDate(p.dueDate)}</TableCell>
                    <TableCell className="text-sm">{p.tasksDone}/{p.tasksTotal}</TableCell>
                    <TableCell className="w-36"><div className="flex items-center gap-2"><Progress value={p.progress} /><span className="text-xs text-slate-500">{p.progress}%</span></div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {list.length === 0 && <p className="p-8 text-center text-sm text-slate-400">No projects found.</p>}
        </Card>
      )}
    </div>
  );
}
